import React,{useState} from 'react'
import {Link,useNavigate} from 'react-router-dom'
import {motion} from 'framer-motion'
import {useAuth} from '../../contexts/AuthContext'
import Layout from '../../components/layout/Layout'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import ThemeToggle from '../../components/ui/ThemeToggle'

const {FiMail,FiLock,FiEye,FiEyeOff,FiStar}=FiIcons

const SignupPage=()=> {
  const {signUp}=useAuth()
  const navigate=useNavigate()
  const [formData,setFormData]=useState({email: '',password: '',confirmPassword: ''})
  const [showPassword,setShowPassword]=useState(false)
  const [showConfirmPassword,setShowConfirmPassword]=useState(false)
  const [loading,setLoading]=useState(false)
  const [errors,setErrors]=useState({})

  const validateForm=()=> {
    const newErrors={}

    if (!formData.email) {
      newErrors.email='Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email='Please enter a valid email'
    }

    if (!formData.password) {
      newErrors.password='Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password='Password must be at least 6 characters'
    }

    if (formData.password !==formData.confirmPassword) {
      newErrors.confirmPassword='Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length===0
  }

  const handleSubmit=async (e)=> {
    e.preventDefault()
    if (!validateForm()) return

    setLoading(true)
    setErrors({})

    try {
      console.log('Submitting signup form...')
      const {data,error}=await signUp(
        formData.email.trim(),
        formData.password
      )

      if (error) {
        console.error('Signup failed:',error)
        // Handle specific error cases
        if (error.message?.includes('User already registered')) {
          setErrors({email: 'An account with this email already exists. Please sign in instead.'})
        } else if (error.message?.includes('Invalid email')) {
          setErrors({email: 'Please enter a valid email address.'})
        } else if (error.message?.includes('Password')) {
          setErrors({password: error.message})
        } else {
          setErrors({general: error.message || 'Failed to create account. Please try again.'})
        }
      } else if (data) {
        console.log('Signup successful')
        // Check if email confirmation is required
        if (data.user && !data.user.email_confirmed_at) {
          // Email verification required
          navigate('/login',{
            state: {
              message: 'Account created successfully! Please check your email and click the verification link to complete your signup.'
            }
          })
        } else {
          // No email verification required,proceed to onboarding
          // The auth state change will handle navigation
        }
      }
    } catch (error) {
      console.error('Unexpected signup error:',error)
      setErrors({general: 'An unexpected error occurred. Please try again.'})
    } finally {
      setLoading(false)
    }
  }

  const handleChange=(e)=> {
    const {name,value}=e.target
    setFormData(prev=> ({...prev,[name]: value}))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev=> ({...prev,[name]: ''}))
    }
    if (errors.general) {
      setErrors(prev=> ({...prev,general: ''}))
    }
  }

  return (
    <Layout showNavbar={false}>
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        {/* Theme Toggle - Fixed Position */}
        <div className="fixed top-4 right-4 z-50">
          <ThemeToggle variant="simple" size="sm" />
        </div>

        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
          className="max-w-md w-full"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiStar} className="text-white text-2xl" />
              </div>
              <span className="font-display font-bold text-2xl bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Chore Haven
              </span>
            </Link>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Start Your Family Adventure!
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Create your account and transform chores into fun
            </p>
          </div>

          {/* Signup Form */}
          <Card>
            {errors.general && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                      errors.email ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                      errors.password ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={()=> setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                      errors.confirmPassword ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={()=> setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  id="terms"
                  type="checkbox"
                  required
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                />
                <label htmlFor="terms" className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                  I agree to the{' '}
                  <Link 
                    to="/terms" 
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-500 hover:underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link 
                    to="/privacy" 
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-500 hover:underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>

            {/* Sign In Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500">
                  Sign in here
                </Link>
              </p>
            </div>
          </Card>

          {/* Benefits */}
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{delay: 0.3}}
            className="mt-8 text-center"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Join thousands of families who've transformed their chore routine!
            </p>
            <div className="flex justify-center items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
              <span>✅ Free to start</span>
              <span>✅ No credit card required</span>
              <span>✅ Set up in 60 seconds</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  )
}

export default SignupPage